import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Beaker, PlayCircle, Award, ClipboardCheck, RotateCcw, ArrowRight, Check } from "lucide-react";
import { FurtherReading } from "@/components/FurtherReading";
import { SaveSession } from "@/components/SaveSession";
import { URINE_COLORS, PAD_CHART } from "@/data/urinalysis";

/* ── Specimen tube (photorealistic glass, colour + turbidity) ─────────────── */
// Clarity is judged the real-world way — by how clearly the printed text on the
// card BEHIND the tube can be read through the sample (clear = readable, cloudy
// = faint, turbid = hidden). The glass, meniscus, reflections and suspended
// particles are all rendered to look like a real bench specimen while staying
// driven by each case's colour + turbidity.
const PRINT_ROWS = [
  [[36, 22], [62, 30], [98, 14]],
  [[36, 42], [82, 18]],
  [[36, 16], [56, 28], [88, 18]],
  [[36, 34], [74, 26]],
  [[36, 24], [64, 16], [84, 24]],
  [[36, 40], [80, 16]],
  [[36, 18], [58, 32]],
  [[36, 30], [70, 30]],
];

export const SampleTube = ({ colorKey, turbidity }) => {
  const fill = URINE_COLORS[colorKey] || "#eccf38";
  const cfg =
    turbidity === "turbid"
      ? { liquid: 0.98, haze: 0.32, spk: 95, cardBlur: 3.4 }
      : turbidity === "cloudy"
      ? { liquid: 0.88, haze: 0.18, spk: 46, cardBlur: 1.8 }
      : { liquid: 0.6, haze: 0, spk: 0, cardBlur: 0.7 }; // clear → see-through

  const specks = useMemo(
    () =>
      Array.from({ length: cfg.spk }, () => {
        // settle particles toward the bottom of the column
        const t = Math.pow(Math.random(), 0.6);
        return {
          cx: 61 + Math.random() * 38,
          cy: 92 + t * 156,
          rx: 0.5 + Math.random() * 1.7,
          ry: 0.5 + Math.random() * 1.4,
          o: 0.25 + Math.random() * 0.5,
        };
      }),
    [cfg.spk]
  );

  const glassBody = "M55 40 L55 232 A25 25 0 0 0 105 232 L105 40 Z";
  const interior = "M59 60 L59 232 A21 21 0 0 0 101 232 L101 60 Z";

  return (
    <svg
      viewBox="0 0 160 300"
      className="h-64 w-auto"
      role="img"
      aria-label="Urine specimen in a glass tube held over printed text"
      data-testid="specimen-tube"
    >
      <defs>
        <clipPath id="tubeInterior"><path d={interior} /></clipPath>
        <filter id="cardRefract" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation={cfg.cardBlur} />
        </filter>
        <filter id="sedSoft" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="0.5" />
        </filter>
        {/* vertical depth of the liquid column */}
        <linearGradient id="liquidV" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={fill} stopOpacity="0.82" />
          <stop offset="0.4" stopColor={fill} stopOpacity="1" />
          <stop offset="1" stopColor={fill} stopOpacity="1" />
        </linearGradient>
        {/* horizontal cylinder shading: dark edges, off-centre specular highlight */}
        <linearGradient id="cylinder" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#000000" stopOpacity="0.32" />
          <stop offset="0.12" stopColor="#000000" stopOpacity="0.05" />
          <stop offset="0.34" stopColor="#ffffff" stopOpacity="0.34" />
          <stop offset="0.46" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="0.78" stopColor="#000000" stopOpacity="0.08" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.42" />
        </linearGradient>
        {/* glass body sheen */}
        <linearGradient id="glassCyl" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#8aa0ad" stopOpacity="0.4" />
          <stop offset="0.14" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="0.3" stopColor="#ffffff" stopOpacity="0.06" />
          <stop offset="0.5" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="0.82" stopColor="#6f8794" stopOpacity="0.12" />
          <stop offset="1" stopColor="#516874" stopOpacity="0.4" />
        </linearGradient>
        <radialGradient id="benchGrad" cx="0.5" cy="0.3" r="0.95">
          <stop offset="0" stopColor="#fcfdfe" />
          <stop offset="1" stopColor="#e4ebf0" />
        </radialGradient>
      </defs>

      {/* bench backdrop */}
      <rect x="0" y="0" width="160" height="300" fill="url(#benchGrad)" />

      {/* printed card behind the tube — blurred more as the sample gets cloudier
          (refraction / read-through clarity test) */}
      <g filter="url(#cardRefract)">
        <rect x="26" y="74" width="108" height="172" rx="7" fill="#f7f3e8" stroke="#e4dcc7" strokeWidth="1.5" />
        {PRINT_ROWS.map((row, r) =>
          row.map(([x, w], i) => (
            <rect key={`${r}-${i}`} x={x} y={90 + r * 19} width={w} height="5" rx="2.5" fill="#9a8f76" opacity="0.9" />
          ))
        )}
      </g>

      {/* soft contact shadow under the tube */}
      <ellipse cx="80" cy="270" rx="34" ry="7" fill="#1f2d36" opacity="0.14" />

      {/* glass body (translucent) */}
      <path d={glassBody} fill="#eef4f7" fillOpacity="0.08" stroke="#aebeca" strokeWidth="2" />

      {/* liquid (clipped to the interior) */}
      <g clipPath="url(#tubeInterior)">
        <rect x="55" y="74" width="50" height="186" fill="url(#liquidV)" opacity={cfg.liquid} />
        {cfg.haze > 0 && <rect x="55" y="74" width="50" height="186" fill="#ffffff" opacity={cfg.haze} />}
        {/* cylindrical curvature + specular band over the liquid */}
        <rect x="55" y="74" width="50" height="186" fill="url(#cylinder)" />
        {/* suspended / settling particles */}
        <g filter="url(#sedSoft)">
          {specks.map((d, i) => (
            <ellipse key={i} cx={d.cx} cy={d.cy} rx={d.rx} ry={d.ry} fill="#ffffff" opacity={d.o} />
          ))}
        </g>
        {/* denser, shadowed base of the column */}
        <ellipse cx="80" cy="248" rx="22" ry="12" fill="#000000" opacity="0.18" />
        {/* concave meniscus: elliptical surface seen at a slight angle */}
        <ellipse cx="80" cy="76" rx="25" ry="7" fill={fill} opacity={Math.min(1, cfg.liquid + 0.1)} />
        <ellipse cx="80" cy="76" rx="25" ry="7" fill="#000000" opacity="0.16" />
        <ellipse cx="80" cy="74.5" rx="20" ry="4.2" fill="#ffffff" opacity="0.5" />
      </g>

      {/* glass sheen + bright specular reflection */}
      <path d={glassBody} fill="url(#glassCyl)" />
      <rect x="63" y="56" width="5.5" height="180" rx="2.75" fill="#ffffff" opacity="0.6" />
      <rect x="71" y="62" width="2" height="170" rx="1" fill="#ffffff" opacity="0.32" />
      <rect x="96" y="70" width="2.5" height="150" rx="1.25" fill="#ffffff" opacity="0.14" />

      {/* frosted write-on label near the top (above the liquid line) */}
      <rect x="58" y="45" width="44" height="23" rx="2" fill="#fbfbfb" opacity="0.9" />
      <rect x="58" y="45" width="44" height="23" rx="2" fill="none" stroke="#d7dde2" strokeWidth="0.8" />
      {/* faint barcode + a hand-written ink line */}
      {[62, 64, 65.5, 68, 70, 71, 73.5, 75].map((bx, i) => (
        <rect key={i} x={bx} y="49" width={i % 3 === 0 ? 1.4 : 0.7} height="7" fill="#7a828a" opacity="0.8" />
      ))}
      <path d="M62 63 q6 -3 12 0 t12 0" fill="none" stroke="#4f6cb0" strokeWidth="1.1" opacity="0.7" />

      {/* glass rim / lip (elliptical opening) */}
      <ellipse cx="80" cy="40" rx="25" ry="6" fill="#dce6ec" stroke="#aebeca" strokeWidth="1.6" />
      <ellipse cx="80" cy="40" rx="20" ry="4" fill="#cdd9e2" opacity="0.6" />
      <ellipse cx="80" cy="40" rx="25" ry="6" fill="none" stroke="#ffffff" strokeWidth="1" opacity="0.6" />

      {/* crisp glass outline on top */}
      <path d="M55 40 L55 232 A25 25 0 0 0 105 232 L105 40" fill="none" stroke="#93a6b3" strokeWidth="1.4" />
    </svg>
  );
};

/* ── Refractometer ocular field with a USG scale + shadow line ─────────────── */
export const Refractometer = ({ usg }) => {
  const SCALE_MIN = 1.0;
  const SCALE_MAX = 1.06;
  const yTop = 28;
  const yBot = 212;
  const yFor = (v) => yTop + ((v - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * (yBot - yTop);
  const boundary = yFor(usg);
  const ticks = [];
  for (let v = SCALE_MIN; v <= SCALE_MAX + 1e-9; v += 0.005) ticks.push(Number(v.toFixed(3)));
  return (
    <svg viewBox="0 0 240 240" className="h-60 w-60" role="img" aria-label="Refractometer view">
      <defs>
        <clipPath id="scopeClip">
          <circle cx="120" cy="120" r="104" />
        </clipPath>
        <linearGradient id="brightField" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#eaf6ff" />
          <stop offset="1" stopColor="#cfeaff" />
        </linearGradient>
      </defs>
      <circle cx="120" cy="120" r="110" fill="#0b1622" />
      <circle cx="120" cy="120" r="104" fill="#1b3a52" />
      <g clipPath="url(#scopeClip)">
        {/* bright (above) / shadow (below) split at the boundary */}
        <rect x="16" y="16" width="208" height={boundary - 16} fill="url(#brightField)" />
        <rect x="16" y={boundary} width="208" height={224 - boundary} fill="#13415e" />
        <line x1="16" y1={boundary} x2="224" y2={boundary} stroke="#0a2330" strokeWidth="2.5" />
        {/* central scale */}
        <line x1="120" y1={yTop} x2="120" y2={yBot} stroke="#0a2330" strokeWidth="1.5" opacity="0.4" />
        {ticks.map((v) => {
          const major = Math.abs(v * 1000 - Math.round(v * 100) * 10) < 1; // .00 / .01 ...
          const y = yFor(v);
          const dark = y >= boundary;
          return (
            <g key={v}>
              <line
                x1={major ? 96 : 108}
                y1={y}
                x2={144}
                y2={y}
                stroke={dark ? "#bcd6e6" : "#1c4660"}
                strokeWidth={major ? 1.5 : 0.8}
                opacity={dark ? 0.85 : 0.7}
              />
              {major && (
                <text
                  x="150"
                  y={y + 3}
                  fontFamily="monospace"
                  fontSize="9"
                  fontWeight="700"
                  fill={dark ? "#dceaf3" : "#234a63"}
                >
                  {v.toFixed(3)}
                </text>
              )}
            </g>
          );
        })}
      </g>
      <circle cx="120" cy="120" r="104" fill="none" stroke="#33566e" strokeWidth="2" />
    </svg>
  );
};

const Swatch = ({ color, label, selected, correct, wrong, onClick, disabled, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className={`flex flex-col items-center gap-1 rounded-md p-1 transition-all ${
      selected ? "ring-2 ring-offset-1 ring-[#b45309]" : "ring-1 ring-slate-200"
    } ${correct ? "ring-2 ring-emerald-500" : ""} ${wrong ? "ring-2 ring-red-500" : ""}`}
  >
    <span className="w-9 h-7 rounded" style={{ backgroundColor: color }} />
    <span className="text-[9px] font-semibold text-slate-500">{label}</span>
  </button>
);

/* ── Dipstick matching grid ───────────────────────────────────────────────── */
export const DipstickMatch = ({ rows, selected, onSelect, revealed }) => (
  <div className="space-y-3" data-testid="dipstick-grid">
    {rows.map((r) => {
      const pad = PAD_CHART[r.key];
      const padColor = pad.levels[r.correct].c;
      const sel = selected[r.key];
      return (
        <div key={r.key} className="rounded-lg border border-slate-200 p-2.5" data-testid={`dipstick-row-${r.key}`}>
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-center gap-1 w-16 shrink-0">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{pad.name}</span>
              <span
                className="w-12 h-8 rounded border border-slate-300 shadow-inner"
                style={{ backgroundColor: padColor }}
                title="reagent pad"
                data-testid={`dipstick-pad-${r.key}`}
              />
              <span className="text-[8px] text-slate-400">your pad</span>
            </div>
            <div className="text-slate-300 text-lg">→</div>
            <div className="flex flex-wrap gap-1.5">
              {pad.levels.map((lvl, i) => (
                <Swatch
                  key={lvl.l}
                  color={lvl.c}
                  label={lvl.l}
                  selected={sel === i && !revealed}
                  correct={revealed && i === r.correct}
                  wrong={revealed && sel === i && sel !== r.correct}
                  disabled={revealed}
                  onClick={() => onSelect(r.key, i)}
                  testid={`dipstick-${r.key}-level-${i}`}
                />
              ))}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

export const UrinalysisIntro = ({ caseCount, onStart }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm fade-up text-center max-w-2xl mx-auto">
    <div className="w-14 h-14 rounded-xl bg-[#b45309]/10 flex items-center justify-center mx-auto">
      <Beaker className="w-7 h-7 text-[#b45309]" />
    </div>
    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-4">
      Urinalysis Bench
    </h1>
    <p className="text-base text-slate-600 mt-3 leading-relaxed">
      Work a urine sample across four beats — judge the{" "}
      <span className="font-semibold text-slate-800">gross appearance</span> (colour &amp; clarity),
      read the <span className="font-semibold text-slate-800">refractometer</span> for specific
      gravity, match the <span className="font-semibold text-slate-800">reagent dipstick</span> to
      the reference chart, then interpret the findings. Some samples are abnormal — you'll choose the
      likely condition and get a full explanation of why.
    </p>
    <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
      {["Colour & clarity", "USG / refractometer", "Dipstick reading", "Diagnosis & overview"].map((t) => (
        <span key={t} className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded bg-slate-100 text-slate-600">
          {t}
        </span>
      ))}
    </div>
    <div className="mt-7">
      <Button onClick={onStart} data-testid="start-drill-btn" className="bg-[#b45309] hover:bg-[#92400e] text-white text-base px-6 py-5">
        <PlayCircle className="w-5 h-5 mr-2" /> Work a sample · {caseCount} cases
      </Button>
    </div>
    <FurtherReading topic="urinalysis" className="mt-8 text-left" />
  </div>
);

const bandStyle = (percent) =>
  percent >= 80
    ? { label: "Competent", text: "text-emerald-600", ring: "border-emerald-300 bg-emerald-50" }
    : percent >= 60
    ? { label: "Developing", text: "text-amber-600", ring: "border-amber-300 bg-amber-50" }
    : { label: "Not yet competent", text: "text-red-600", ring: "border-red-300 bg-red-50" };

export const UrinalysisDebrief = ({ result, onNext, onRestart }) => {
  const { percent, breakdown, caseTitle, overview } = result;
  const s = bandStyle(percent);
  return (
    <div className="space-y-5 fade-up">
      <div className="bg-white border border-emerald-200 rounded-xl p-6 shadow-lg" data-testid="urinalysis-debrief">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-semibold mb-5">
          <ClipboardCheck className="w-4 h-4" />
          <span>Urinalysis debrief · {caseTitle}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="flex items-center gap-5">
            <div className={`flex flex-col items-center justify-center w-24 h-24 rounded-2xl border-4 ${s.ring}`} data-testid="debrief-grade">
              <Award className={`w-5 h-5 ${s.text}`} />
              <span className={`text-4xl font-bold leading-none ${s.text}`} data-testid="debrief-percent">{percent}</span>
              <span className="text-[10px] text-slate-400 font-semibold">/100</span>
            </div>
            <div>
              <div className={`text-xl font-bold ${s.text}`} data-testid="debrief-band">{s.label}</div>
              <div className="text-xs text-slate-500 mt-1">Cert IV urinalysis competency</div>
            </div>
          </div>

          <div className="lg:col-span-2" data-testid="debrief-breakdown">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Stage scores</div>
            <div className="space-y-2">
              {breakdown.map((b) => {
                const pct = Math.round((b.earned / b.max) * 100);
                return (
                  <div key={b.key} data-testid={`debrief-stage-${b.key}`}>
                    <div className="flex items-center justify-between text-xs mb-0.5">
                      <span className="text-slate-700 font-medium">{b.label}</span>
                      <span className="font-mono-clinical font-semibold text-slate-800">{b.earned}/{b.max}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444" }} />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{b.detail}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {overview && (
          <div className="mt-5 rounded-lg bg-[#b45309]/5 border border-[#b45309]/20 p-4" data-testid="debrief-overview">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#b45309] font-semibold mb-1">
              <Check className="w-3.5 h-3.5" /> What this looks like
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{overview}</p>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={onNext} data-testid="next-case-btn" className="bg-[#b45309] hover:bg-[#92400e] text-white">
            Next sample <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <Button onClick={onRestart} variant="outline" data-testid="restart-btn" className="text-slate-600">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Back to start
          </Button>
        </div>
      </div>

      <SaveSession
        simulatorId="urinalysis"
        simulatorName="Urinalysis Bench"
        score={percent}
        grade={s.label}
        summary={`${caseTitle} · ${s.label}`}
      />

      <FurtherReading topic="urinalysis" />
    </div>
  );
};
